import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Download, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { dokumenteApi } from '@/services/api';
import { documentCategoryLabels, type BauvorhabenDocument, type DocumentCategory } from '@/types';
import { formatDateOnly } from '@/utils/progress';
import { formatDateTime } from '@/utils/currency';
import { documentMimeGroup, documentGroupIcon, formatFileSize } from '@/components/dokumente/documentDisplay';

/**
 * Globale Dokumente-Übersicht (cross-BV, Menüpunkt "Dokumente"): Suche,
 * Kategorie-Filter + Pagination. Die Dokumente selbst leben im
 * Bauvorhaben-Detail — diese Seite ist der Einstieg über alle Projekte.
 *
 * BV-Link → /bauvorhaben/{id}: Nutzer mit dokumente-, aber ohne
 * bauvorhaben-Modul landen auf der ModuleDisabledPage — bewusst
 * akzeptiert (die Module werden praktisch immer gemeinsam freigegeben).
 */

const CATEGORY_ORDER = Object.keys(documentCategoryLabels) as DocumentCategory[];
const PER_PAGE = 50;

export function DokumentePage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<'' | DocumentCategory>('');
  const [page, setPage] = useState(1);
  const [documents, setDocuments] = useState<BauvorhabenDocument[]>([]);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  // Suche mit 300 ms Debounce (kein Request pro Tastendruck).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    dokumenteApi
      .getAll({ page, per_page: PER_PAGE, search: search || undefined, category: category || undefined })
      .then((response) => {
        if (cancelled) return;
        setDocuments(response.data.data);
        setPagination({
          total: response.data.pagination?.total ?? response.data.data.length,
          total_pages: response.data.pagination?.total_pages ?? 1,
        });
        setError('');
      })
      .catch((err) => {
        if (!cancelled) setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Laden der Dokumente');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, search, category]);

  const handleDownload = (id: number, fileName: string | null) => {
    setDownloadingId(id);
    dokumenteApi
      .getFileBlob(id)
      .then((response) => {
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `dokument-${id}`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Fehler beim Herunterladen'))
      .finally(() => setDownloadingId(null));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Dokumente</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* Suche */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Titel oder Dateiname suchen…"
              className="input h-9 w-64 pl-8 pr-8 text-sm"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                title="Suche löschen"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-700"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* Kategorie-Filter */}
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value as '' | DocumentCategory);
              setPage(1);
            }}
            className="input h-9 py-1 text-sm"
            aria-label="Kategorie filtern"
          >
            <option value="">Alle Kategorien</option>
            {CATEGORY_ORDER.map((cat) => (
              <option key={cat} value={cat}>{documentCategoryLabels[cat]}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2.5 font-medium">Dokument</th>
                <th className="px-4 py-2.5 font-medium">Kategorie</th>
                <th className="px-4 py-2.5 font-medium">Bauvorhaben</th>
                <th className="px-4 py-2.5 font-medium">Datum</th>
                <th className="px-4 py-2.5 font-medium">Hochgeladen</th>
                <th className="px-4 py-2.5 text-right font-medium">Größe</th>
                <th className="px-4 py-2.5" aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary-600" />
                  </td>
                </tr>
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    {search || category
                      ? 'Keine Dokumente für diese Suche gefunden.'
                      : 'Noch keine Dokumente vorhanden — sie werden im Bauvorhaben-Detail hochgeladen.'}
                  </td>
                </tr>
              ) : (
                documents.map((doc) => {
                  const group = documentMimeGroup(doc.file_mime, doc.original_name);
                  const Icon = documentGroupIcon[group];
                  return (
                    <tr key={doc.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-50 text-gray-500">
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0">
                            <span className="block max-w-[280px] truncate font-medium text-gray-900" title={doc.original_name ?? undefined}>
                              {doc.title || doc.original_name || `Dokument #${doc.id}`}
                            </span>
                            {doc.gewerk_name && (
                              <span className="block truncate text-xs text-gray-500">Gewerk: {doc.gewerk_name}</span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                          {documentCategoryLabels[doc.category]}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <Link to={`/bauvorhaben/${doc.bauvorhaben_id}`} className="max-w-[200px] truncate text-primary-600 hover:text-primary-700">
                          {doc.bauvorhaben_name || `BV #${doc.bauvorhaben_id}`}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">
                        {doc.taken_at ? formatDateOnly(doc.taken_at) : formatDateTime(doc.created_at)}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">
                        {doc.uploaded_by_name || '—'}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-right text-gray-600">
                        {formatFileSize(doc.file_size) || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => handleDownload(doc.id, doc.original_name)}
                          disabled={downloadingId === doc.id}
                          title="Herunterladen"
                          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                        >
                          {downloadingId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination (Prev/Next, minimal) */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {pagination.total} Dokumente · Seite {page} von {pagination.total_pages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
              className="btn btn-secondary inline-flex items-center gap-1 px-3 py-1.5 text-sm disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Zurück
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
              disabled={page >= pagination.total_pages || isLoading}
              className="btn btn-secondary inline-flex items-center gap-1 px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Weiter
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}